import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Hero3D from "@/components/Hero3D";
import ProductCard from "@/components/ProductCard";
import NewsletterCTA from "@/components/NewsletterCTA";
import TikTokFeed from "@/components/TikTokFeed";
import usePageMeta, { buildOrganizationSchema, buildWebsiteSchema } from "@/lib/useSeo";
import { HOME } from "@/constants/testIds";
import { ArrowRight, Lightning, ShieldCheck, Truck, Coins } from "@phosphor-icons/react";

export default function Home() {
  const [content, setContent] = useState({});
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);

  usePageMeta({
    title: "Vape Essence · Premium Disposable Vapes & Nicotine Pouches (ZA)",
    description:
      "Shop 100% authentic disposable vapes and nicotine pouches in South Africa. 48-72hr delivery, loyalty points on every order. Elf Bar, ZYN, VELO, GeekBar & more. 18+.",
    image: "https://images.unsplash.com/photo-1611232658409-0dfa5b56b929?w=1200",
    jsonLd: [buildOrganizationSchema({ content }), buildWebsiteSchema()],
  });

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/products", { params: { featured: true } }).then((r) => setProducts(r.data));
    api.get("/products/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="grain">
      {/* HERO */}
      <section className="relative aurora min-h-[80vh] sm:min-h-[92vh] overflow-hidden border-b border-white/10">
        <Hero3D />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,#050505_75%)]" />

        <div className="relative z-10 mx-auto flex min-h-[80vh] sm:min-h-[92vh] max-w-[1400px] flex-col justify-center px-4 py-20 sm:px-6 sm:py-32 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3"
          >
            <span className="pulse-dot inline-block h-2 w-2 rounded-full bg-[#00FFC2]" />
            <span className="font-label text-[10px] sm:text-xs text-white/70">
              {content.hero_tagline || "PREMIUM VAPE & POUCH CULTURE"}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            data-testid={HOME.heroTitle}
            className="mt-6 sm:mt-8 font-display text-5xl font-black leading-[0.9] tracking-tighter sm:text-7xl lg:text-[8rem]"
          >
            Ignite <span className="text-[#FF5A1F] glow-pink">Your</span>
            <br />
            <span className="text-[#00FFC2] glow-cyan">Senses</span>
            <span className="text-white">.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-6 sm:mt-8 max-w-xl text-sm sm:text-lg leading-relaxed text-white/70"
          >
            {content.hero_subtitle ||
              "Premium disposable vapes & nicotine pouches. Curated. Authentic. Delivered."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-8 sm:mt-12 flex flex-wrap gap-3 sm:gap-4"
          >
            <Link
              to="/shop"
              data-testid={HOME.heroCta}
              className="group inline-flex items-center gap-2 sm:gap-3 bg-[#FF5A1F] px-5 sm:px-8 py-3 sm:py-4 font-label text-[11px] sm:text-xs text-black transition-transform hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(255,90,31,0.35)]"
            >
              Shop the drop
              <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/shop?cat=Nicotine Pouches"
              data-testid={HOME.categoryPouches}
              className="inline-flex items-center gap-2 sm:gap-3 border border-white/20 px-5 sm:px-8 py-3 sm:py-4 font-label text-[11px] sm:text-xs hover:border-[#00FFC2] hover:text-[#00FFC2] transition-colors"
            >
              Nicotine Pouches
            </Link>
            <Link
              to="/shop?cat=Disposable Vapes"
              data-testid={HOME.categoryVapes}
              className="inline-flex items-center gap-2 sm:gap-3 border border-white/20 px-5 sm:px-8 py-3 sm:py-4 font-label text-[11px] sm:text-xs hover:border-[#FFC900] hover:text-[#FFC900] transition-colors"
            >
              Disposable Vapes
            </Link>
            <Link
              to="/quiz"
              data-testid="hero-quiz-cta"
              className="inline-flex items-center gap-2 sm:gap-3 border border-[#FFC900]/60 bg-[#FFC900]/5 px-5 sm:px-8 py-3 sm:py-4 font-label text-[11px] sm:text-xs text-[#FFC900] hover:bg-[#FFC900] hover:text-black transition-colors"
            >
              ✨ Find my flavour
            </Link>
          </motion.div>
        </div>

        {/* Marquee at bottom */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-black/70 backdrop-blur overflow-x-hidden">
          <div className="marquee-track py-3 sm:py-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex shrink-0 items-center gap-8 sm:gap-16 pr-8 sm:pr-16 font-label text-[10px] sm:text-xs text-white/50">
                <span className="text-[#FF5A1F]">◆</span>48-72 HOUR DELIVERY IN JOHANNESBURG
                <span className="text-[#00FFC2]">◆</span>EARN 5% BACK IN POINTS (EX-VAT)
                <span className="text-[#FFC900]">◆</span>100% AUTHENTIC PRODUCTS
                <span className="text-[#FF5A1F]">◆</span>18+ AGE-VERIFIED CHECKOUT
                <span className="text-[#00FFC2]">◆</span>SECURE PAYFAST PAYMENTS
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STARTER KIT PROMO BANNER */}
      <section className="mx-auto max-w-[1400px] px-4 pt-10 sm:px-6 sm:pt-14 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="home-starter-banner"
          className="relative overflow-hidden border border-[#FF5A1F]/50 bg-gradient-to-r from-[#0d0605] via-[#150804] to-[#0a0d0c]"
        >
          {/* Ambient glow */}
          <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-[#FF5A1F]/20 blur-3xl" />
          <div className="pointer-events-none absolute -right-24 -bottom-24 h-64 w-64 rounded-full bg-[#00FFC2]/15 blur-3xl" />

          <div className="relative grid gap-4 sm:gap-6 sm:grid-cols-[1fr_auto] items-center p-5 sm:p-8">
            <div>
              <div className="inline-flex items-center gap-2 font-label text-[10px] tracking-widest text-[#FFC900]">
                <span className="inline-block h-2 w-2 rounded-full bg-[#FFC900] animate-pulse" />
                LIMITED-TIME BUNDLE
              </div>
              <h2 className="mt-3 font-display text-2xl font-black leading-tight tracking-tighter sm:text-3xl md:text-4xl">
                Save <span className="text-[#FFC900]">R50</span> when buying 3{" "}
                <span className="text-[#00FFC2]">Disposables</span>.
              </h2>
              <p className="mt-2 text-sm text-white/70 max-w-xl">
                3 Disposables — hand picked by yourself, any flavour. Perfect first buy or gift.
              </p>
            </div>
            <Link
              to="/shop?cat=Bundles"
              data-testid="home-starter-banner-cta"
              className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-[#FF5A1F] px-6 py-3 font-label text-[11px] text-black transition-transform hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgba(255,90,31,0.35)] whitespace-nowrap"
            >
              GRAB THEM NOW
              <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* PILLARS */}
      <section className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 sm:py-24 lg:px-10 lg:py-32">
        <div className="grid gap-3 sm:gap-6 grid-cols-2 md:grid-cols-4">
          {[
            { Icon: ShieldCheck, title: "100% Authentic", body: "Every batch verified. No knock-offs.", color: "#FF5A1F" },
            { Icon: Truck, title: "Fast Delivery", body: "48-72hr dispatch nationwide.", color: "#00FFC2" },
            { Icon: Coins, title: "Loyalty Points", body: "Earn on every rand. Redeem instantly.", color: "#FFC900" },
            { Icon: Lightning, title: "Fresh Flavours", body: "New flavours monthly. Never boring.", color: "#FF5A1F" },
          ].map(({ Icon, title, body, color }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="border border-white/10 bg-[#0d0d0d] p-4 sm:p-8 hover:border-white/25 transition-colors"
            >
              <Icon size={24} weight="duotone" color={color} />
              <div className="mt-4 sm:mt-6 font-display text-sm sm:text-lg font-bold">{title}</div>
              <p className="mt-1 sm:mt-2 text-[11px] sm:text-sm text-white/60">{body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-16 lg:px-10">
        <div className="flex items-end justify-between">
          <div>
            <div className="font-label text-xs text-[#00FFC2]">/ FEATURED</div>
            <h2 className="mt-2 sm:mt-3 font-display text-2xl font-black tracking-tighter sm:text-4xl lg:text-5xl">
              The drop <span className="text-white/50">everyone's</span> vaping.
            </h2>
          </div>
          <Link
            to="/shop"
            className="hidden md:inline-flex items-center gap-2 font-label text-xs text-white/60 hover:text-white transition-colors"
          >
            All products <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        <div
          data-testid={HOME.featuredGrid}
          className="mt-6 sm:mt-10 grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
        >
          {products.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* CATEGORY SPLIT */}
      <section className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 sm:py-24 lg:px-10 lg:py-32">
        <div className="grid gap-3 sm:gap-6 md:grid-cols-2">
          <Link
            to="/shop?cat=Disposable Vapes"
            className="group relative overflow-hidden border border-white/10 bg-[#0d0d0d] p-6 sm:p-10 h-[280px] sm:h-[380px] flex flex-col justify-end"
          >
            <img
              src="https://images.unsplash.com/photo-1618601208267-baa5b780b70e?w=1200"
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="relative z-10">
              <div className="font-label text-[10px] sm:text-xs text-[#FF5A1F]">/ CATEGORY</div>
              <div className="mt-1 sm:mt-2 font-display text-2xl sm:text-4xl font-black tracking-tighter">Disposable Vapes</div>
              <div className="mt-1 sm:mt-2 text-xs sm:text-sm text-white/60">5K to 15K puffs. Every flavour imaginable.</div>
            </div>
          </Link>
          <Link
            to="/shop?cat=Nicotine Pouches"
            className="group relative overflow-hidden border border-white/10 bg-[#0d0d0d] p-6 sm:p-10 h-[280px] sm:h-[380px] flex flex-col justify-end"
          >
            <img
              src="https://images.unsplash.com/photo-1659806343945-949fb46def50?w=1200"
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="relative z-10">
              <div className="font-label text-[10px] sm:text-xs text-[#00FFC2]">/ CATEGORY</div>
              <div className="mt-1 sm:mt-2 font-display text-2xl sm:text-4xl font-black tracking-tighter">Nicotine Pouches</div>
              <div className="mt-1 sm:mt-2 text-xs sm:text-sm text-white/60">Tobacco-free. Ice-cold. All-white.</div>
            </div>
          </Link>
        </div>
      </section>

      <NewsletterCTA />
      {content.tiktok_urls?.length > 0 && <TikTokFeed urls={content.tiktok_urls} />}
    </div>
  );
}
